import { Injectable } from '@angular/core';

import { AuthentificationService } from '@app/authentification/authentification.service';
import {
  Pledge,
  PledgeLegalEntityChoices,
  PledgeRead,
} from '@app/forms/subscription/definition/interface/Pledge';
import { AuditPage } from '@app/forms/subscription/definition/interface/audit-page';
import { PledgeOptions } from '@app/forms/subscription/definition/interface/pledge-options';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { Observable, of, map, shareReplay, switchMap, tap } from 'rxjs';
import { Dao } from '../dao';
import { DataLoaderAuthentified } from '../data-loader-authentified';
import {
  API_ROUTE_PLEDGE,
  API_ROUTE_PLEDGES,
  API_ROUTE_PLEDGES_INIT,
  API_ROUTE_PLEDGES_LEGAL_ENTITY_CHOICES,
  Api_ROUTE_PLEDGE_OPTIONS,
} from './api-router';
import {
  RcdaRecommendationAiAvailabilityDto,
  RcdaRecommendationProposalDto,
} from './dto/rcda-recommendation-ai.dto';

@Injectable({
  providedIn: 'root',
})
export class PledgeDao extends Dao {
  private pledges$: DataLoaderAuthentified<PledgeRead[]> =
    new DataLoaderAuthentified<PledgeRead[]>(this.getAuthentified(), user => {
      return this.httpApi.get(
        API_ROUTE_PLEDGES.replace(':accountId', String(user.accountId)),
      );
    });

  constructor(
    httpApi: HttpApiService,
    authentificationService: AuthentificationService,
  ) {
    super(httpApi, authentificationService);
  }

  /* Pladge */
  getPledges$(): Observable<PledgeRead[]> {
    return this.pledges$.get().pipe(
      map((pledges: PledgeRead[]) => {
        pledges.forEach(
          (pledge: PledgeRead) =>
            (pledge.owners = pledge.owners.sort((a, b) => {
              return a?.isMain ? -1 : 1;
            })),
        );

        return pledges;
      }),
    );
  }

  initPledge(
    productType: string = 'ASSURANCE_VIE',
    productName: string = undefined,
    ownerIds: number[] = undefined,
    productIds: number[] = undefined,
    sellerIds: number[] = undefined,
    physicalUser: boolean = true,
    gridkyPropertyId: number = undefined,
    loanId: number = undefined,
    borrowerInsuranceContext: string = undefined,
    borrowerInsuranceMode: string = undefined,
    portfolioSimulationParameters = undefined,
    operationAssetContractId: number = undefined,
    operation: string = 'SUBSCRIPTION',
    scope: 'LEGAL_ONLY' | 'FULL' | null = null,
    // Détenteurs + part de détention (`share`). Remplace `ownerIds` côté crédit.
    owners: Array<{ ownerId: number; share?: number }> = undefined,
    sellerLegalEntities: Record<number, number> = undefined,
    attachedLegalEntityId: number = undefined,
  ): Observable<Pledge> {
    return this.command(user =>
      this.httpApi.post(
        API_ROUTE_PLEDGES_INIT.replace(':accountId', String(user.accountId)),
        {
          productType,
          productName,
          ownerIds,
          owners,
          productIds,
          sellerIds,
          physicalUser,
          gridkyPropertyId,
          loanId,
          borrowerInsuranceContext,
          borrowerInsuranceMode,
          portfolioSimulationParameters,
          operation,
          operationAssetContractId,
          // Séquençage V2 — périmètre de souscription envoyé au backend.
          scope,
          sellerLegalEntities,
          attachedLegalEntityId,
        },
      ),
    ).pipe(
      tap(() => this.pledges$.reset()),
      shareReplay(1),
    );
  }

  getLegalEntityChoices(
    productType: string,
    productId: number = undefined,
  ): Observable<PledgeLegalEntityChoices> {
    const params: { [param: string]: string } = { productType };
    if (productId) {
      params.productId = String(productId);
    }
    return this.command(user =>
      this.httpApi.get(
        API_ROUTE_PLEDGES_LEGAL_ENTITY_CHOICES.replace(
          ':accountId',
          String(user.accountId),
        ),
        params,
      ),
    );
  }

  getPledgeById(id): Observable<PledgeRead> {
    return this.pledges$.get().pipe(
      switchMap((pledges: PledgeRead[]) => {
        let pledge = pledges.find((p: PledgeRead) => p.id === id);
        return of(pledge);
      }),
    );
  }
  getPledge(id): Observable<PledgeRead> {
    return this.command(user =>
      this.httpApi.get(
        API_ROUTE_PLEDGE.replace(':accountId', String(user.accountId)).replace(
          ':pledgeId',
          id,
        ),
      ),
    ).pipe(shareReplay(1));
  }

  savePledge(entity: PledgeRead): Observable<PledgeRead> {
    const dto = { ...entity };

    const url = API_ROUTE_PLEDGE.replace(
      ':accountId',
      dto.accountId + '',
    ).replace(':pledgeId', dto.id + '');

    return this.httpApi.put(url, dto).pipe(tap(() => this.pledges$.reset()));
  }
  deletePledge(pledge: PledgeRead): Observable<PledgeRead> {
    const dto = { ...pledge };
    const url = API_ROUTE_PLEDGE.replace(
      ':accountId',
      dto.accountId + '',
    ).replace(':pledgeId', dto.id + '');

    let sub = this.httpApi.delete(url);
    return sub;
  }

  getClientAssets(accountId: number, assetType: string): Observable<any> {
    return this.httpApi.post(
      `/accounts/${accountId}/assets/financial?type=${assetType}`,
      {
        source: 'INTERNAL',
        hasAssetsSupport: true,
      },
    );
  }

  checkNeedSequencage(pledgeId: number, entity: PledgeRead): Observable<any> {
    return this.httpApi.post(
      `/pledges/${pledgeId}/check-need-sequencage`,
      entity,
    );
  }

  getRcdaRecommendationAiAvailability(
    pledgeId: number,
  ): Observable<RcdaRecommendationAiAvailabilityDto> {
    return this.command(user =>
      this.httpApi.get(
        `/accounts/${user.accountId}/pledges/${pledgeId}/rcda-recommandation/ai-availability`,
      ),
    );
  }

  proposeRcdaRecommendation(
    pledgeId: number,
  ): Observable<RcdaRecommendationProposalDto> {
    return this.command(user =>
      this.httpApi.post(
        `/accounts/${user.accountId}/pledges/${pledgeId}/rcda-recommandation/ai-proposal`,
        {},
      ),
    );
  }

  getProAuditLink(pledgeId: number, page: AuditPage): Observable<string> {
    return this.command(user =>
      this.httpApi.get(
        `/accounts/${user.accountId}/pledges/${pledgeId}/pro-audit-link`,
        { page },
      ),
    );
  }

  getPledgeOptions$(): Observable<PledgeOptions> {
    return this.httpApi.get(Api_ROUTE_PLEDGE_OPTIONS);
  }

  reset() {
    this.pledges$?.reset();
  }

  onUserChange(event: any) {
    this?.reset();
  }
}
